/**
 * @module app.core
 */

import { autoinject, singleton } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { ProviderService, IProviderGroupSearchFilters } from 'app/cd/services/provider-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { ProviderGroupSearchCustomEvent } from 'app/core/components/provider-groups/provider-group-search-form';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { SaveProviderGroup } from 'app/core/components/provider-groups/save-provider-group';
import { SaveProviderContracts } from 'app/core/components/provider-groups/save-provider-contracts';
import * as CD from 'app/cd/models/essette-api';

@singleton()
@autoinject()
export class ProviderGroupSearch extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    protected providerGroupSearchFilters: IProviderGroupSearchFilters = {};
    protected tempProviderGroupSearchFilters: IProviderGroupSearchFilters = {};

    protected providerGroupResults: CD.ProviderGroup[] = [];
    protected $isValid!: boolean;
    protected validateForm = validate;
    protected editedProviderGroup: CD.ProviderGroup | null = null;

    private sorting: Sorting = new Sorting();
    private defaultSorting: Sorting = new Sorting();
    protected resultCount: number = 0;

    protected pageSize: number = 50;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $providerGroupSearch!: ProviderGroupSearchCustomEvent;
    protected $pager!: EssPagerCustomElement;
    protected $modalProviderGroup!: IPopup;
    protected $modalProviderContracts!: IPopup;
    protected $editProviderGroupForm!: IForm;
    protected $vmSaveProviderGroup!: SaveProviderGroup;
    protected $vmSaveProviderContracts!: SaveProviderContracts;

    ////****************** Event Subscriptions **************************************////


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly service: ProviderService,
        private readonly toaster: Toaster,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {

        super(...rest);
        this.defaultSorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.loadCustomFieldTargets();
    }

    ////****************** Events ***************************************************////

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        if(!!this.$modalProviderGroup) {
            this.searchProviderGroups();
            this.$modalProviderGroup.close();
        }
    }

    ////****************** Functions ***********************************************////
    public async reload(): Promise<void> {
        await this.searchProviderGroups();
    }

    protected submitSearchProviderGroups() {
        this.providerGroupSearchFilters = { ...this.tempProviderGroupSearchFilters };
        this.searchProviderGroups();
    }

    protected editProviderGroup(providerGroup: CD.ProviderGroup): void {
        this.$vmSaveProviderGroup.providerGroup = providerGroup;
        this.$vmSaveProviderGroup.loadProviderGroup();
        this.editedProviderGroup = providerGroup;
        this.$vmSaveProviderGroup.inModal = true;
        this.$modalProviderGroup.open();
    }

    protected editProviderContracts(providerGroup: CD.ProviderGroup): void {
        this.$vmSaveProviderContracts.providerGroup = providerGroup;
        this.editedProviderGroup = providerGroup;
        this.$modalProviderContracts.open();
    }

    protected editProviderGroupClose(): void {
        if(this.$editProviderGroupForm.reset) this.$editProviderGroupForm.reset();
        this.$modalProviderGroup.close();
    }

    protected editProviderContractsClose(): void {
        this.$modalProviderContracts.close();
    }

    protected formProviderGroupSuccess({ detail }: FormSuccessCustomEvent): void {
        if(detail.changes && (<CD.ProviderGroup>detail.changes).id) {
            this.editedProviderGroup = <CD.ProviderGroup>detail.changes;
        }
        if(this.$modalProviderGroup) {
            this.$modalProviderGroup.close();

            this.clearResults();
            this.searchProviderGroups();
        }
    }

    protected formProviderContractsSuccess({ detail }: FormSuccessCustomEvent): void {
        if(detail.changes && (<CD.ProviderGroup>detail.changes).id) {
            this.editedProviderGroup = <CD.ProviderGroup>detail.changes;
        }
        if(this.$modalProviderGroup) {
            this.$modalProviderGroup.close();
        }
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        if(this.resultCount) this.reload();
    }

    protected clearResults() {
        this.providerGroupResults = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateProviderGroup(providerGroup: CD.ProviderGroup): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(providerGroup.isActive) {
            const result = await confirmation.showDeactivate('cd:providerGroup.$long', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateProviderGroup(providerGroup.id!, !providerGroup.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('cd:providerGroup.$long', !providerGroup.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.ProviderGroup');
    }

    @apiCall
    private async searchProviderGroups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting.entries.length > 0 ? this.sorting : this.defaultSorting,
            includeInactive: false
        };

        const includes = [
            'address.billing',
            'address.physical',
            'phoneNumber.primary',
            'phoneNumber.fax'
        ];

        const fields = [
            'address.billing.city',
            'address.billing.postalCode',
            'address.billing.region.*',
            'address.billing.street1',
            'address.billing.street2',
            'address.physical.city',
            'address.physical.postalCode',
            'address.physical.region.*',
            'address.physical.street1',
            'address.physical.street2',
            'custom.lookup.*',
            'custom.text.*',
            'description',
            'emailAddress',
            'identifier.external',
            'identifier.nationalProvider',
            'phoneNumber.primary.country.phoneNumber.callingCode',
            'phoneNumber.primary.extension',
            'phoneNumber.primary.number',
            'phoneNumber.fax.country.phoneNumber.callingCode',
            'phoneNumber.fax.extension',
            'phoneNumber.fax.number',
            'websiteUrl'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.searchProviderGroups(this.providerGroupSearchFilters, options, includes, fields);
            this.providerGroupResults = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
